import { Slot } from "@radix-ui/react-slot"
import { IconLoader2 } from "@tabler/icons-react"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"

import { cn } from "@/utils/tailwind"

const buttonVariants = cva(
  "relative flex cursor-pointer items-center justify-center rounded-md font-medium whitespace-nowrap transition-colors select-none focus-visible:ring-1 focus-visible:ring-slate-900 focus-visible:outline-hidden active:translate-y-px aria-disabled:pointer-events-none aria-disabled:opacity-70",
  {
    variants: {
      variant: {
        primary: "",
        secondary: "",
        outline: "",
        subtle: "",
      },
      size: {
        xs: cn("h-7 px-3.5 text-xs"),
        sm: cn("h-8 px-4 text-sm"),
        md: cn("h-10 px-5 text-base"),
        lg: cn("h-12 px-7 text-lg"),
      },
      color: {
        instacover: "",
        gray: "",
      },
    },
    defaultVariants: {
      size: "md",
      variant: "primary",
      color: "instacover",
    },
    compoundVariants: [
      {
        color: "gray",
        variant: "primary",
        className: cn("bg-slate-700 text-slate-100 hover:bg-slate-700/80"),
      },
      {
        color: "gray",
        variant: "secondary",
        className: cn("bg-slate-200 text-slate-700 hover:bg-slate-100/80"),
      },
      {
        color: "gray",
        variant: "outline",
        className: cn("border border-slate-200 bg-white text-slate-700 hover:bg-slate-100"),
      },
      {
        color: "gray",
        variant: "subtle",
        className: cn("bg-transparent text-slate-700 hover:bg-slate-100"),
      },
      {
        color: "instacover",
        variant: "primary",
        className: cn("bg-instacover-500 text-instacover-50 hover:bg-instacover-600/90"),
      },
      {
        color: "instacover",
        variant: "secondary",
        className: cn("bg-instacover-100 text-instacover-600 hover:bg-instacover-200/70"),
      },
      {
        color: "instacover",
        variant: "outline",
        className: cn("border-instacover-600 text-instacover-600 hover:bg-instacover-100/90 border bg-white"),
      },
      {
        color: "instacover",
        variant: "subtle",
        className: cn("text-instacover-600 hover:bg-instacover-100/90 bg-transparent"),
      },
    ],
  },
)

const spinnerVariants = cva("animate-spin", {
  variants: {
    size: {
      xs: cn("size-3"),
      sm: cn("size-4"),
      md: cn("size-5"),
      lg: cn("size-6"),
    },
  },
})

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color">,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  isLoading?: boolean
  ref?: React.RefObject<HTMLButtonElement>
}

const Button = (props: ButtonProps) => {
  const {
    className,
    variant,
    size,
    color,
    isLoading,
    asChild = false,
    children,
    onClick,
    disabled,
    ref,
    ...rest
  } = props

  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, color, className }))}
      ref={ref}
      aria-disabled={isLoading || disabled}
      onClick={(e) => {
        if (isLoading || disabled) {
          return
        }

        onClick?.(e)
      }}
      {...rest}
    >
      {isLoading ? (
        <div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <IconLoader2 className={cn(spinnerVariants({ size }))} />
          </div>

          <div className="invisible block">{children}</div>
        </div>
      ) : (
        children
      )}
    </Comp>
  )
}

export { Button, buttonVariants }
